import React, { memo, useContext } from "react";

import { getCardBorderStyle } from "@/app/application/theme";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Grid,
	InputAdornment,
	List,
	ListItem,
	Stack,
	TablePagination,
	TextField,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { MeasurementsDaily } from "@/model/Application/Settings/SettingsResponseTypes";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/** Shared panel/card style (aligned with Procedure Updates, Compare, Checklist editor, Archive) */
const getPanelCardSx = (t: Theme) => ({
	borderRadius: 2,
	border: getCardBorderStyle(t),
	boxShadow:
		t.palette.mode === "dark"
			? "0 1px 0 0 rgba(255,255,255,0.05)"
			: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
	backgroundColor: t.palette.background.paper,
});

/** Thin scrollbar for panels (matches Procedure Updates, Archive) */
const scrollbarSx = (t: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 3,
		background:
			t.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
	},
	"&::-webkit-scrollbar-thumb:hover": {
		background:
			t.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
	},
});

interface LastSamplesListProps {
	searchTerm: string;
	handleSearch: (event: React.ChangeEvent<HTMLInputElement>) => void;
	paginatedMeasurements: MeasurementsDaily[];
	handleDialogOpen: (labMeasurementId: number, labAnalyzeId: number) => void;
	selectedMeasurement: { LabMeasurementId: number | null };
	timezone: string;
	getUserNameById: (userId: number) => string;
	filteredMeasurements: MeasurementsDaily[];
	itemsPerPage: number;
	handlePageChange: (
		event: React.MouseEvent<HTMLButtonElement> | null,
		page: number,
	) => void;
	page: number;
	itemsPerPageOptions: number[];
	handleItemsPerPageChange: (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => void;
}

const LastSamplesList: React.FC<LastSamplesListProps> = ({
	searchTerm,
	handleSearch,
	paginatedMeasurements,
	handleDialogOpen,
	selectedMeasurement,
	timezone,
	getUserNameById,
	filteredMeasurements,
	itemsPerPage,
	handlePageChange,
	page,
	itemsPerPageOptions,
	handleItemsPerPageChange,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	return (
		<Card
			variant="outlined"
			sx={{
				...getPanelCardSx(theme),
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minHeight: 0,
				overflow: "hidden",
			}}
		>
			<CardHeader
				title={
					<Stack spacing={0.75}>
						<Typography
							variant="caption"
							sx={{
								fontWeight: 800,
								letterSpacing: "0.08em",
								color: "primary.main",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Auto.Features.Lab.Ui.Components.LastSamplesList.L122C8.History">History</Translate>
						</Typography>
						<Stack direction="row" spacing={1} alignItems="center" useFlexGap>
							<Typography
								variant="h6"
								sx={{ fontWeight: 800, lineHeight: 1.15 }}
							>
								<Translate String="LabDaily.LastSamples">Last samples</Translate>
							</Typography>
							<Chip
								size="small"
								label={`${filteredMeasurements.length}`}
								sx={{
									height: 22,
									borderRadius: 999,
									fontWeight: 700,
									bgcolor: "background.paper",
									border: "1px solid",
									borderColor: "divider",
								}}
							/>
						</Stack>
					</Stack>
				}
				action={
					<TextField
						placeholder={GetLanguageValueAsString(translation, "Auto.Features.Lab.Ui.Components.LastSamplesList.L149C19.SearchSamples", "Search samples")}
						variant="outlined"
						value={searchTerm}
						onChange={handleSearch}
						size="small"
						sx={{
							width: isMobile ? "100%" : 240,
							"& .MuiOutlinedInput-root": {
								borderRadius: 2.5,
								bgcolor: "background.paper",
								"&.Mui-focused": {
									boxShadow: (t) =>
										`0 0 0 3px ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.24 : 0.16)}`,
								},
							},
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/>
				}
				sx={(t) => ({
					py: isMobile ? 1.25 : 1.5,
					px: isMobile ? 2 : 2.5,
					bgcolor: t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.03) : "grey.50",
					borderBottom: 1,
					borderColor: "divider",
					borderRadius: "12px 12px 0 0",
					"& .MuiCardHeader-action": {
						m: 0,
						alignSelf: "center",
					},
					"& .MuiCardHeader-content": { minWidth: 0 },
					flexDirection: isMobile ? "column" : "row",
					gap: isMobile ? 1.5 : 0,
				})}
			/>
			<CardContent
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					pt: 2,
					px: isMobile ? 2 : 2.5,
					pb: 2,
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						...scrollbarSx(theme),
					}}
				>
					<List sx={{ p: 0 }}>
						{paginatedMeasurements.map((item) => {
							const isSelected =
								item.LabMeasurementId === selectedMeasurement.LabMeasurementId;
							const isDark = theme.palette.mode === "dark";
							const borderAlpha = isDark ? 0.4 : 0.35;
							const hoverShadow = isDark
								? `0 8px 24px ${alpha(theme.palette.common.black, 0.4)}`
								: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`;

							return (
								<ListItem
									key={item.LabMeasurementId}
									onClick={() =>
										handleDialogOpen(item.LabMeasurementId, item.LabAnalyzeId)
									}
									sx={{
										p: 1.5,
										mb: 1,
										borderRadius: 2,
										border: `1px solid ${
											isSelected
												? alpha(theme.palette.primary.main, 0.5)
												: alpha(theme.palette.divider, borderAlpha)
										}`,
										bgcolor: isSelected
											? alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08)
											: "background.paper",
										cursor: "pointer",
										transition: "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
										"&:hover": {
											bgcolor: isSelected
												? alpha(
														theme.palette.primary.main,
														isDark ? 0.16 : 0.12,
													)
												: "action.hover",
											borderColor: isSelected
												? alpha(theme.palette.primary.main, 0.7)
												: theme.palette.divider,
											boxShadow: hoverShadow,
										},
										"&:last-child": {
											mb: 0,
										},
									}}
								>
									<Grid
										container
										spacing={1.5}
										alignItems="flex-start"
										sx={{ width: "100%" }}
									>
										<Grid item xs={12} sm={8}>
											<Stack spacing={0.75}>
												<Typography
													variant="subtitle2"
													sx={{
														fontWeight: 600,
														lineHeight: 1.4,
														color: "text.primary",
													}}
												>
													{item.TagId}
													{item.TagDescription && (
														<Typography
															component="span"
															variant="body2"
															sx={{
																ml: 0.5,
																color: "text.secondary",
																fontWeight: 400,
															}}
															>
																{" - "}{item.TagDescription}
															</Typography>
													)}
												</Typography>

												<Typography
													variant="body2"
													sx={{ fontWeight: 500, color: "text.primary" }}
												>
													{item.ParameterName}:{" "}
													{item.Measurement != null && (
														<>
															<Box
																component="span"
																sx={{
																	px: 1,
																	py: 0.25,
																	backgroundColor:
																		item.Measurement < item.MinLimit ||
																		item.Measurement > item.MaxLimit
																			? alpha(theme.palette.error.main, 0.12)
																			: alpha(theme.palette.success.main, 0.12),
																	color:
																		item.Measurement < item.MinLimit ||
																		item.Measurement > item.MaxLimit
																			? "error.dark"
																			: "success.dark",
																	borderRadius: 999,
																	fontWeight: 600,
																	border: "1px solid",
																	borderColor:
																		item.Measurement < item.MinLimit ||
																		item.Measurement > item.MaxLimit
																			? alpha(theme.palette.error.main, 0.25)
																			: alpha(theme.palette.success.main, 0.25),
																}}
															>
																{item.Measurement}
															</Box>{" "}
															<Typography
																component="span"
																variant="caption"
																color="text.secondary"
															>
																(Min: {item.MinLimit}, Max: {item.MaxLimit})
															</Typography>
														</>
													)}
												</Typography>

												<Stack
													direction="row"
													spacing={1.5}
													flexWrap="wrap"
													useFlexGap
												>
													<Typography variant="caption" color="text.secondary">
														<Translate String="LabDaily.TestDate">
															Test Date:{" "}
														</Translate>
														{dayjs
															.utc(item.TestDate)
															.tz(timezone)
															.format("DD/MM/YY HH:mm:ss")}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														<Translate String="LabDaily.SaveDate">
															Save Date:{" "}
														</Translate>
														{dayjs
															.utc(item.SaveDate)
															.tz(timezone)
															.format("DD/MM/YY HH:mm:ss")}
													</Typography>
												</Stack>
											</Stack>
										</Grid>

										<Grid item xs={12} sm={4}>
											<Stack
												spacing={0.75}
												alignItems={{ xs: "flex-start", sm: "flex-end" }}
											>
												<Typography variant="caption" color="text.secondary">
													<Translate String="LabDaily.User">User </Translate>
													{getUserNameById(item.UserId)} –{" "}
													{dayjs
														.utc(item.SaveDate)
														.tz(timezone)
														.format("HH:mm:ss")}
												</Typography>
												{item.Comment && (
													<Typography
														variant="caption"
														color="text.secondary"
														sx={{
															fontStyle: "italic",
															textAlign: { xs: "left", sm: "right" },
														}}
													>
														<Translate String="LabDaily.Comment">
															Comment:{" "}
														</Translate>
														{item.Comment}
													</Typography>
												)}
											</Stack>
										</Grid>
									</Grid>
								</ListItem>
							);
						})}
					</List>
				</Box>

				<Box
					sx={{
						mt: 1.5,
						pt: 1,
						borderTop: 1,
						borderColor: "divider",
					}}
				>
					<TablePagination
						component="div"
						count={filteredMeasurements.length}
						page={page - 1}
						onPageChange={(_event, newPage) =>
							handlePageChange(_event, newPage + 1)
						}
						rowsPerPage={itemsPerPage}
						onRowsPerPageChange={handleItemsPerPageChange}
						rowsPerPageOptions={itemsPerPageOptions}
						labelRowsPerPage={
							<span style={{ whiteSpace: "nowrap" }}>
								<Translate String="LabDaily.ItemsPerPage">
									Items per page:
								</Translate>
							</span>
						}
						sx={{
							"& .MuiTablePagination-toolbar": {
								px: 0,
								minHeight: 40,
								gap: isMobile ? 1 : 2,
							},
						}}
					/>
				</Box>
			</CardContent>
		</Card>
	);
};

export default memo(LastSamplesList);
